import React, { useState, useEffect } from 'react';
import './Animated.css';

type AnimatedProps = {
  children?: React.ReactNode;
  animationIn?: string;
  animationOut?: string;
  animationInDuration?: number;
  animationOutDuration?: number;
  animationInDelay?: number;
  animationOutDelay?: number;
  isVisible?: boolean;
  animateOnMount?: boolean;
  style?: React.CSSProperties;
  className?: string;
};

const Animated: React.FC<AnimatedProps> = ({
  style,
  children,
  className = '',
  isVisible = true,
  animationInDelay = 0,
  animationOutDelay = 0,
  animateOnMount = true,
  animationIn = 'fadeIn',
  animationOut = 'fadeOut',
  animationInDuration = 1000,
  animationOutDuration = 1000,
}) => {
  const [shouldRender, setShouldRender] = useState(isVisible || animateOnMount);
  const [animationClass, setAnimationClass] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setIsAnimating(true);

      const delayTimer = setTimeout(() => {
        setAnimationClass(animationIn);
      }, animationInDelay);

      const durationTimer = setTimeout(() => {
        setIsAnimating(false);
      }, animationInDelay + animationInDuration);

      return () => {
        clearTimeout(delayTimer);
        clearTimeout(durationTimer);
      };
    } else {
      setIsAnimating(true);
      setAnimationClass(animationOut);

      const hideTimer = setTimeout(() => {
        setShouldRender(false);
        setIsAnimating(false);
      }, animationOutDuration + animationOutDelay);

      return () => {
        clearTimeout(hideTimer);
      };
    }
  }, [
    isVisible,
    animationIn,
    animationOut,
    animationInDuration,
    animationOutDuration,
    animationInDelay,
    animationOutDelay,
  ]);

  if (!shouldRender) return null;

  const duration = isVisible ? animationInDuration : animationOutDuration;

  return (
    <div
      className={`brij-animated ${animationClass} ${className} max-w-full`}
      style={{
        animationDuration: `${duration}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export default Animated;
